import { useRef } from 'react';
import { FolderOpen } from 'lucide-react';

export interface ContactListData {
  file: File | null;
}

export const INITIAL_CONTACT_LIST_DATA: ContactListData = {
  file: null
};

export function ContactListStep(props: { data: ContactListData; onChange: (data: ContactListData) => void }) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (file: File | null) => {
    props.onChange({ file });
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && file.name.endsWith('.csv')) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    if (file) {
      handleFileSelect(file);
    }
  };

  return (
    <div>
      <h2 className='text-xl font-semibold text-foreground mb-6'>Select Contact list</h2>

      <div className='space-y-6'>
        <div>
          <p className='text-sm font-medium text-foreground mb-3'>Upload list</p>
          <DropZone file={props.data.file} onDrop={handleDrop} onDragOver={handleDragOver} onClick={handleClick} />
          <input ref={fileInputRef} type='file' accept='.csv' onChange={handleInputChange} className='hidden' />
        </div>

        <FileGuidelines />
      </div>
    </div>
  );
}

function DropZone(props: {
  file: File | null;
  onDrop: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onClick: () => void;
}) {
  return (
    <button
      type='button'
      onClick={props.onClick}
      onDrop={props.onDrop}
      onDragOver={props.onDragOver}
      className='w-full border-2 border-dashed border-blue rounded-lg
        p-10 flex flex-col items-center justify-center bg-card
        hover:bg-muted/50 transition-colors cursor-pointer'
    >
      <div
        className='w-16 h-16 rounded-lg bg-blue-500 flex items-center
        justify-center mb-4'
      >
        <FolderOpen className='w-8 h-8 text-blue' />
      </div>
      {props.file ? (
        <div className='text-center'>
          <p className='text-sm font-medium text-foreground'>{props.file.name}</p>
          <p className='text-xs text-muted-foreground mt-1'>Click to replace</p>
        </div>
      ) : (
        <>
          <p className='text-blue font-medium mb-1'>Drag & Drop your CSV files here</p>
          <p className='text-sm text-muted-foreground mb-3'>or click to browse</p>
          <p className='text-xs text-muted-foreground'>Supported Format: CSV with phone number column</p>
        </>
      )}
    </button>
  );
}

function FileGuidelines() {
  return (
    <div className='border border-blue rounded-lg p-5 flex gap-8'>
      <div className='flex-1'>
        <h4 className='text-sm font-semibold text-foreground mb-3'>File Guidelines</h4>
        <p className='text-sm text-muted-foreground mb-2'>
          Your CSV file should include only two columns: <span className='font-semibold text-foreground'>Name</span> and{' '}
          <span className='font-semibold text-foreground'>Phone</span>.
        </p>
        <p className='text-sm text-muted-foreground'>
          Make sure the column headers match these names exactly so the file can be processed correctly.
        </p>
      </div>
      <div>
        <p className='text-sm font-medium text-foreground mb-2'>Example</p>
        <div className='bg-muted rounded-lg p-3 font-mono text-sm'>
          <p className='text-muted-foreground'>Name,Phone</p>
          <p className='text-muted-foreground'>John Doe,+15551234567</p>
          <p className='text-muted-foreground'>Jane Smith,+15559876543</p>
        </div>
      </div>
    </div>
  );
}
